import { SocialAuthMutationVariables } from '@graphql/mutations/__generated__/auth.generated'
import { Divider } from '@mui/material'
import Button from '@mui/material/Button'
import { Form, Formik } from 'formik'
import React, { useMemo } from 'react'
import * as Yup from 'yup'
import FormikErrors from '../../../Formik/FormikErrors'
import SocialAuthButtons from '../../../SocialAuthButtons'
import SignUpFormFields from './SignUpFormFields'
import { useTranslation } from 'react-i18next'

export interface ISignUpFormData {
  firstName: string
  lastName: string
  email: string
  password: string
  captcha: string
  subscriptions: boolean
  policyAgreement: boolean
}

interface IProps {
  formErrors?: { messages: string[]; field?: string }[] | null

  onSubmit(values: ISignUpFormData): Promise<void>

  variant?: 'dialog' | 'default'

  onSubmitSocialSignIn?(values: SocialAuthMutationVariables): Promise<void>

  handleCloseModal?(): void

  handleSignIn?(): void
}

const SignUpForm: React.FC<IProps> = ({
  onSubmit,
  formErrors,
  variant,
  handleCloseModal,
  onSubmitSocialSignIn,
  handleSignIn,
}) => {
  const { t, i18n } = useTranslation()
  const schema = useMemo(
    () =>
      Yup.object().shape<ISignUpFormData>({
        firstName: Yup.string()
          .min(2, t('formErrors.tooShort', 'Too Short!'))
          .max(30, t('formErrors.tooLong', 'Too Long!'))
          .required(t('formErrors.valueIsRequired', 'This value is required')),
        lastName: Yup.string(),
        email: Yup.string()
          .email(t('formErrors.invalidEmail', 'Invalid email'))
          .required(t('formErrors.valueIsRequired', 'This value is required')),
        password: Yup.string()
          .min(2, t('formErrors.tooShort', 'Too Short!'))
          .max(30, t('formErrors.tooLong', 'Too Long!'))
          .required(t('formErrors.valueIsRequired', 'This value is required')),
        captcha: Yup.string().required(t('formErrors.valueIsRequired', 'This value is required')),
        subscriptions: Yup.boolean(),
        policyAgreement: Yup.boolean().oneOf([true], t('formErrors.fieldMustBeChecked', 'Field must be checked')),
      }),
    []
  )
  return (
    <Formik<ISignUpFormData>
      initialValues={{
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        captcha: '',
        subscriptions: true,
        policyAgreement: true,
      }}
      validationSchema={schema}
      onSubmit={async values => {
        await onSubmit(values)
      }}
    >
      <>
        <FormikErrors errors={formErrors} />
        {(variant === 'default' || !variant) && (
          <Form>
            <legend className='legend text-base'>
              {t('accountDetails', 'Account details')}
              <span className='legend-label'>{t('required', 'Required')}</span>
            </legend>
            <SignUpFormFields />
            <div className='mt-10'>
              <Button type='submit' variant='contained' color='secondary' size='large' fullWidth>
                <span className='font-semibold tracking-widest'>{t('signUp', 'Sign up')}</span>
              </Button>
            </div>
          </Form>
        )}
        {variant === 'dialog' && (
          <>
            <Form>
              <SignUpFormFields handleClickPrivacyPolicy={() => handleCloseModal()} />
              <div className='mt-10'>
                <Button type='submit' variant='contained' color='secondary' size='large' fullWidth>
                  <span className='font-semibold tracking-widest'>{t('signUp', 'Sign up')}</span>
                </Button>
                <SocialAuthButtons onSubmitSocialSignIn={onSubmitSocialSignIn} register />
              </div>
            </Form>
            <div className='my-20'>
              <Divider />
            </div>
            <div className='flex items-center justify-center'>
              <span className='font-bold'>{t('alreadyHaveAnAccount', 'Already have an account?')}</span>
              <span>
                <Button variant='text' color='secondary' onClick={handleSignIn} disableRipple>
                  <span className='font-bold text-capitalize'>{t('signIn', 'Sign in')}</span>
                </Button>
              </span>
            </div>
          </>
        )}
      </>
    </Formik>
  )
}

export default SignUpForm
